/**
 * exportForAI — generates a detailed markdown report of all performance data
 * optimized for LLM analysis (Claude, ChatGPT, etc.)
 */

import { ArticlePerformanceRow } from '../../../../services/performanceMetricsService';

function fmt(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 10000) return `${(n / 1000).toFixed(0)}k`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(Math.round(n));
}

function fmtTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

function sum7d(series: number[], offset: number): number {
  return series.slice(-(offset + 7), offset === 0 ? undefined : -offset).reduce((s, v) => s + v, 0);
}

function sumField(rows: ArticlePerformanceRow[], field: string): number {
  return rows.reduce((s, r) => s + (Number((r as any)[field]) || 0), 0);
}

export function generateAIExport(rows: ArticlePerformanceRow[], contentLabel: string): string {
  const now = new Date().toISOString().slice(0, 10);
  const lines: string[] = [];

  // --- Header ---
  lines.push(`# Talaera ${contentLabel} Performance Report`);
  lines.push(`Generated: ${now} | Window: Last 28 days | Articles: ${rows.length}`);
  lines.push('');
  lines.push('> This report contains detailed performance data for every article. Use it to identify trends, opportunities, and areas for improvement.');
  lines.push('');

  // --- Site-Wide Summary ---
  const totalImpressions = sumField(rows, 'impressions');
  const totalClicks = sumField(rows, 'clicks');
  const totalSessions = sumField(rows, 'ga4_sessions');
  const totalUsers = sumField(rows, 'ga4_total_users');
  const totalEngDuration = sumField(rows, 'ga4_engagement_duration');
  const totalScrolled = sumField(rows, 'ga4_scrolled_users');
  const totalConversions = sumField(rows, 'ga4_conversions');
  const totalAiSessions = sumField(rows, 'ga4_ai_sessions');
  const totalAioImpr = sumField(rows, 'ai_overview_impressions');
  const totalAioClicks = sumField(rows, 'ai_overview_clicks');

  const avgReadTime = totalUsers > 0 ? totalEngDuration / totalUsers : 0;
  const scrollRate = totalUsers > 0 ? totalScrolled / totalUsers : 0;
  const overallCtr = totalImpressions > 0 ? totalClicks / totalImpressions : 0;

  // WoW
  const allClicksSeries: number[] = [];
  const allImprSeries: number[] = [];
  const allSessSeries: number[] = [];
  const maxLen = Math.max(...rows.map(r => (r.clicks_series || []).length), 0);
  for (let i = 0; i < maxLen; i++) {
    let c = 0, im = 0, se = 0;
    for (const r of rows) {
      c += (r.clicks_series || [])[i] || 0;
      im += (r.impressions_series || [])[i] || 0;
      se += ((r as any).ga4_sessions_series || [])[i] || 0;
    }
    allClicksSeries.push(c);
    allImprSeries.push(im);
    allSessSeries.push(se);
  }

  const wowClicks = sum7d(allClicksSeries, 0);
  const wowClicksPrev = sum7d(allClicksSeries, 7);
  const wowImpr = sum7d(allImprSeries, 0);
  const wowImprPrev = sum7d(allImprSeries, 7);
  const wowSess = sum7d(allSessSeries, 0);
  const wowSessPrev = sum7d(allSessSeries, 7);

  function pctChange(curr: number, prev: number): string {
    if (prev === 0) return curr > 0 ? '+∞' : '0%';
    const pct = ((curr - prev) / prev * 100).toFixed(1);
    return Number(pct) >= 0 ? `+${pct}%` : `${pct}%`;
  }

  lines.push('## Site-Wide Summary (28 days)');
  lines.push('');
  lines.push('| Metric | Value |');
  lines.push('|--------|-------|');
  lines.push(`| Total Impressions | ${fmt(totalImpressions)} |`);
  lines.push(`| Total Clicks | ${fmt(totalClicks)} |`);
  lines.push(`| Overall CTR | ${(overallCtr * 100).toFixed(2)}% |`);
  lines.push(`| Total Sessions (GA4) | ${fmt(totalSessions)} |`);
  lines.push(`| Unique Users | ${fmt(totalUsers)} |`);
  lines.push(`| Avg Read Time | ${fmtTime(avgReadTime)} |`);
  lines.push(`| Scroll Depth (90%) | ${(scrollRate * 100).toFixed(1)}% |`);
  lines.push(`| Conversions | ${totalConversions} |`);
  lines.push(`| AI Referral Sessions | ${totalAiSessions} |`);
  lines.push(`| AI Overview Impressions | ${totalAioImpr} |`);
  lines.push('');

  lines.push('### Week over Week');
  lines.push('');
  lines.push('| Metric | This Week | Last Week | Change |');
  lines.push('|--------|-----------|-----------|--------|');
  lines.push(`| Impressions | ${fmt(wowImpr)} | ${fmt(wowImprPrev)} | ${pctChange(wowImpr, wowImprPrev)} |`);
  lines.push(`| Clicks | ${fmt(wowClicks)} | ${fmt(wowClicksPrev)} | ${pctChange(wowClicks, wowClicksPrev)} |`);
  lines.push(`| Sessions | ${fmt(wowSess)} | ${fmt(wowSessPrev)} | ${pctChange(wowSess, wowSessPrev)} |`);
  lines.push('');

  // --- Cluster Breakdown ---
  const clusters: Record<string, ArticlePerformanceRow[]> = {};
  for (const r of rows) {
    const key = r.pillar || 'Uncategorized';
    if (!clusters[key]) clusters[key] = [];
    clusters[key].push(r);
  }

  lines.push('## Performance by Content Cluster');
  lines.push('');
  lines.push('| Cluster | Articles | Impressions | Clicks | CTR | Sessions | Avg Read Time | Scroll 90% | Eng Rate | AI Sessions | Assessment |');
  lines.push('|---------|----------|-------------|--------|-----|----------|---------------|------------|----------|-------------|------------|');

  const siteMedianImprPerArticle = median(
    Object.values(clusters).map(arts => arts.reduce((s, a) => s + (a.impressions || 0), 0) / arts.length)
  );
  const siteMedianCtr = median(
    Object.values(clusters).map(arts => {
      const imp = arts.reduce((s, a) => s + (a.impressions || 0), 0);
      const clk = arts.reduce((s, a) => s + (a.clicks || 0), 0);
      return imp > 0 ? clk / imp : 0;
    })
  );

  const clusterEntries = Object.entries(clusters)
    .map(([name, arts]) => {
      const imp = arts.reduce((s, a) => s + (a.impressions || 0), 0);
      const clk = arts.reduce((s, a) => s + (a.clicks || 0), 0);
      const sess = arts.reduce((s, a) => s + (a.ga4_sessions || 0), 0);
      const users = arts.reduce((s, a) => s + ((a as any).ga4_total_users || 0), 0);
      const engDur = arts.reduce((s, a) => s + ((a as any).ga4_engagement_duration || 0), 0);
      const scrolled = arts.reduce((s, a) => s + ((a as any).ga4_scrolled_users || 0), 0);
      const aiSess = arts.reduce((s, a) => s + ((a as any).ga4_ai_sessions || 0), 0);
      const ctr = imp > 0 ? clk / imp : 0;
      const readTime = users > 0 ? engDur / users : 0;
      const scroll = users > 0 ? scrolled / users : 0;

      let brW = 0, brS = 0;
      for (const a of arts) {
        const s = a.ga4_sessions || 0;
        const b = a.ga4_bounce_rate;
        if (s > 0 && b != null) { brW += b * s; brS += s; }
      }
      const engRate = brS > 0 ? 1 - brW / brS : 0;

      const highVis = (imp / arts.length) > siteMedianImprPerArticle;
      const highEff = ctr > siteMedianCtr;
      let assessment = 'Needs attention';
      if (arts.length < 5) assessment = 'Low sample';
      else if (highVis && highEff) assessment = 'Strong — write more';
      else if (highVis && !highEff) assessment = 'Visible but not compelling';
      else if (!highVis && highEff) assessment = 'Niche but effective';

      return { name, arts, imp, clk, sess, ctr, readTime, scroll, engRate, aiSess, assessment };
    })
    .sort((a, b) => b.clk - a.clk);

  for (const c of clusterEntries) {
    const shortName = c.name.replace(/^Cluster \d+:\s*/, '');
    lines.push(`| ${shortName} | ${c.arts.length} | ${fmt(c.imp)} | ${fmt(c.clk)} | ${(c.ctr * 100).toFixed(2)}% | ${fmt(c.sess)} | ${fmtTime(c.readTime)} | ${(c.scroll * 100).toFixed(0)}% | ${(c.engRate * 100).toFixed(0)}% | ${c.aiSess} | ${c.assessment} |`);
  }
  lines.push('');

  // --- Funnel Stage Breakdown ---
  const funnels: Record<string, ArticlePerformanceRow[]> = {};
  for (const r of rows) {
    const key = r.funnel_stage || 'unknown';
    if (!funnels[key]) funnels[key] = [];
    funnels[key].push(r);
  }

  lines.push('## Performance by Funnel Stage');
  lines.push('');
  lines.push('| Funnel Stage | Articles | Impressions | Clicks | CTR | Sessions |');
  lines.push('|--------------|----------|-------------|--------|-----|----------|');
  for (const [stage, arts] of Object.entries(funnels).sort((a, b) => b[1].length - a[1].length)) {
    const imp = arts.reduce((s, a) => s + (a.impressions || 0), 0);
    const clk = arts.reduce((s, a) => s + (a.clicks || 0), 0);
    const sess = arts.reduce((s, a) => s + (a.ga4_sessions || 0), 0);
    const ctr = imp > 0 ? clk / imp : 0;
    lines.push(`| ${stage} | ${arts.length} | ${fmt(imp)} | ${fmt(clk)} | ${(ctr * 100).toFixed(2)}% | ${fmt(sess)} |`);
  }
  lines.push('');

  // --- AI Visibility ---
  if (totalAiSessions > 0) {
    const aiSources: Record<string, number> = {};
    for (const r of rows) {
      const sources = (r as any).ga4_ai_sources;
      if (sources && typeof sources === 'object') {
        for (const [src, cnt] of Object.entries(sources)) {
          aiSources[src] = (aiSources[src] || 0) + Number(cnt || 0);
        }
      }
    }

    lines.push('## AI Visibility');
    lines.push('');
    lines.push(`Total AI referral sessions: ${totalAiSessions}`);
    lines.push('');
    lines.push('| AI Source | Sessions |');
    lines.push('|----------|----------|');
    for (const [src, cnt] of Object.entries(aiSources).sort((a, b) => b[1] - a[1])) {
      lines.push(`| ${src} | ${cnt} |`);
    }
    lines.push('');

    const topAiArticles = [...rows]
      .filter(r => ((r as any).ga4_ai_sessions || 0) > 0)
      .sort((a, b) => ((b as any).ga4_ai_sessions || 0) - ((a as any).ga4_ai_sessions || 0))
      .slice(0, 10);

    if (topAiArticles.length > 0) {
      lines.push('### Top Articles by AI Referral Traffic');
      lines.push('');
      lines.push('| Article | AI Sessions | Total Sessions | AI % |');
      lines.push('|---------|-------------|----------------|------|');
      for (const a of topAiArticles) {
        const aiS = (a as any).ga4_ai_sessions || 0;
        const totS = a.ga4_sessions || 0;
        lines.push(`| ${a.title} | ${aiS} | ${totS} | ${totS > 0 ? (aiS / totS * 100).toFixed(1) : 0}% |`);
      }
      lines.push('');
    }
  }

  // --- Every Article Detail ---
  lines.push('## All Articles — Full Detail');
  lines.push('');
  lines.push('| # | Title | URL | Pillar | Funnel | Published | Impressions | Clicks | CTR | Position | Sessions | Users | Avg Read | Scroll 90% | Eng Rate | Bounce | Conversions | Entry Sessions | AI Sessions | AIO Impressions |');
  lines.push('|---|-------|-----|--------|--------|-----------|-------------|--------|-----|----------|----------|-------|----------|------------|----------|--------|-------------|----------------|-------------|-----------------|');

  const sorted = [...rows].sort((a, b) => (b.clicks || 0) - (a.clicks || 0));
  sorted.forEach((r, i) => {
    const pillar = (r.pillar || '').replace(/^Cluster \d+:\s*/, '') || '—';
    const funnel = r.funnel_stage || '—';
    const pub = r.published_at ? r.published_at.slice(0, 10) : '—';
    const pos = r.avg_position != null ? r.avg_position.toFixed(1) : '—';
    const users = (r as any).ga4_total_users || 0;
    const engDur = (r as any).ga4_engagement_duration || 0;
    const scrolled = (r as any).ga4_scrolled_users || 0;
    const readTime = users > 0 ? fmtTime(engDur / users) : '—';
    const scroll = users > 0 ? `${(scrolled / users * 100).toFixed(0)}%` : '—';
    const br = r.ga4_bounce_rate != null ? `${(r.ga4_bounce_rate * 100).toFixed(0)}%` : '—';
    const engRate = r.ga4_bounce_rate != null ? `${((1 - r.ga4_bounce_rate) * 100).toFixed(0)}%` : '—';
    const aiSess = (r as any).ga4_ai_sessions || 0;
    const aioImpr = (r as any).ai_overview_impressions || 0;
    const landing = (r as any).ga4_landing_sessions || 0;

    lines.push(`| ${i + 1} | ${r.title} | ${r.url} | ${pillar} | ${funnel} | ${pub} | ${r.impressions} | ${r.clicks} | ${(r.ctr * 100).toFixed(2)}% | ${pos} | ${r.ga4_sessions || 0} | ${users} | ${readTime} | ${scroll} | ${engRate} | ${br} | ${r.ga4_conversions || 0} | ${landing} | ${aiSess} | ${aioImpr} |`);
  });
  lines.push('');

  // --- Opportunities ---
  lines.push('## Opportunities');
  lines.push('');

  const highImprLowClicks = [...rows]
    .filter(r => (r.impressions || 0) > 100 && (r.clicks || 0) < 5)
    .sort((a, b) => (b.impressions || 0) - (a.impressions || 0))
    .slice(0, 10);

  if (highImprLowClicks.length > 0) {
    lines.push('### High Impressions, Low Clicks (title/description may need work)');
    lines.push('');
    for (const r of highImprLowClicks) {
      lines.push(`- **${r.title}** — ${fmt(r.impressions)} impressions, ${r.clicks} clicks, CTR ${(r.ctr * 100).toFixed(2)}%`);
    }
    lines.push('');
  }

  const lowEngagement = [...rows]
    .filter(r => (r.ga4_sessions || 0) > 20 && r.ga4_bounce_rate != null && r.ga4_bounce_rate > 0.7)
    .sort((a, b) => (b.ga4_bounce_rate || 0) - (a.ga4_bounce_rate || 0))
    .slice(0, 10);

  if (lowEngagement.length > 0) {
    lines.push('### High Bounce Rate (>70% — content may not match search intent)');
    lines.push('');
    for (const r of lowEngagement) {
      lines.push(`- **${r.title}** — ${(r.ga4_bounce_rate! * 100).toFixed(0)}% bounce, ${r.ga4_sessions} sessions`);
    }
    lines.push('');
  }

  lines.push('---');
  lines.push(`*Report generated ${now}. Data sources: Google Search Console (impressions, clicks, position, CTR), Google Analytics 4 (sessions, users, engagement, AI referrals). Window: trailing 28 days.*`);

  return lines.join('\n');
}
